import React from 'react';
import { useLocation } from 'react-router-dom';
import { HexColorPicker } from 'react-colorful';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { LocationSuggestInput } from '../../../users/customers/components/LocationSuggestInput';
import type { LandingSection } from '../../../../../components/storefront/landingSectionsCms';
import { DEFAULT_LANDING_SECTIONS } from '../../../../../components/storefront/landingSectionsCms';
import LandingSectionsEditor, {
  cloneSections,
} from '../components/LandingSectionsEditor';
import { FALLBACK_STOREFRONT_THEME } from '../../../../../utils/storefrontTheme';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

function toPickerHex(raw: string): string {
  const t = (raw || '').trim();
  if (/^#[0-9a-fA-F]{6}$/.test(t)) return t.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(t)) {
    const r = t[1];
    const g = t[2];
    const b = t[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return '#64748b';
}

type SiteContent = {
  aboutTitle: string;
  aboutSubtitle: string;
  aboutBody: string;
  aboutImageUrl: string;
  storefrontStatCustomers: string;
  storefrontStatSales: string;
  storefrontStatTowns: string;
  publicContactAddress: string;
  publicContactCountry: string;
  publicContactCity: string;
  publicContactEmail: string;
  publicContactPhone: string;
  publicContactLatitude: string;
  publicContactLongitude: string;
  socialFacebookUrl: string;
  socialInstagramUrl: string;
  socialLinkedinUrl: string;
  socialXUrl: string;
  termsTitle: string;
  termsBody: string;
  privacyTitle: string;
  privacyBody: string;
  cookiesTitle: string;
  cookiesBody: string;
  paymentSecurityTitle: string;
  paymentSecurityBody: string;
  storefrontThemeBodyBg: string;
  storefrontThemeNavBg: string;
  storefrontThemeNavTopBg: string;
  storefrontThemeNavFg: string;
  storefrontThemeNavAccent: string;
  storefrontThemeBrandPrimary: string;
  landingSections?: LandingSection[];
};

type SiteContentTabId = 'overview' | 'contact' | 'legal' | 'landing' | 'translations';

export default function WorkspaceSiteContentPage() {
  const location = useLocation();
  const { t } = useTranslation();
  const { authorizedFetch } = useAuth();
  const globalAuthorizedFetch = React.useCallback(
    (input: RequestInfo | URL, init: RequestInit = {}) => {
      const headers = new Headers(init.headers || {});
      headers.set('x-department-slug', '');
      return authorizedFetch(input, { ...init, headers });
    },
    [authorizedFetch],
  );
  const publicContactRef = React.useRef<HTMLDivElement | null>(null);
  const [data, setData] = React.useState<SiteContent | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [countrySuggestions, setCountrySuggestions] = React.useState<string[]>([]);
  const [citySuggestions, setCitySuggestions] = React.useState<string[]>([]);
  const [addressSuggestions, setAddressSuggestions] = React.useState<
    Array<{ label: string; street: string; city: string; country: string; postalCode: string; latitude?: number; longitude?: number }>
  >([]);
  const [landingSections, setLandingSections] = React.useState<LandingSection[]>([]);
  const [translationLocale, setTranslationLocale] = React.useState('fr');
  const [translationLoading, setTranslationLoading] = React.useState(false);
  const [translationSaving, setTranslationSaving] = React.useState(false);
  const [translationSuccess, setTranslationSuccess] = React.useState<string | null>(null);
  const [siteTranslations, setSiteTranslations] = React.useState<Record<string, string>>({});
  const [landingSectionTranslationsJson, setLandingSectionTranslationsJson] =
    React.useState('[]');
  const [activeTab, setActiveTab] = React.useState<SiteContentTabId>('overview');

  const countryCacheRef = React.useRef(new Map<string, Array<{ name: string; code?: string }>>());
  const cityCacheRef = React.useRef(new Map<string, Array<{ name: string }>>());
  const addressCacheRef = React.useRef(
    new Map<string, Array<{ label: string; street: string; city: string; country: string; postalCode: string; latitude?: number; longitude?: number }>>(),
  );

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await globalAuthorizedFetch(`${API_BASE}/settings/site-content`);
        if (!res.ok) throw new Error('Unable to load site content.');
        const payload = (await res.json()) as SiteContent;
        if (!cancelled) {
          const merged: SiteContent = {
            ...payload,
            storefrontThemeBodyBg:
              payload.storefrontThemeBodyBg?.trim() || FALLBACK_STOREFRONT_THEME.bodyBg,
            storefrontThemeNavBg:
              payload.storefrontThemeNavBg?.trim() || FALLBACK_STOREFRONT_THEME.navBg,
            storefrontThemeNavTopBg:
              payload.storefrontThemeNavTopBg?.trim() || FALLBACK_STOREFRONT_THEME.navTopBg,
            storefrontThemeNavFg:
              payload.storefrontThemeNavFg?.trim() || FALLBACK_STOREFRONT_THEME.navFg,
            storefrontThemeNavAccent:
              payload.storefrontThemeNavAccent?.trim() || FALLBACK_STOREFRONT_THEME.navAccent,
            storefrontThemeBrandPrimary:
              payload.storefrontThemeBrandPrimary?.trim() || FALLBACK_STOREFRONT_THEME.brandPrimary,
          };
          setData(merged);
          setLandingSections(
            cloneSections(merged.landingSections ?? DEFAULT_LANDING_SECTIONS),
          );
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Unable to load.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [globalAuthorizedFetch]);

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      setTranslationLoading(true);
      try {
        const url = new URL(`${API_BASE}/settings/site-content/translations`);
        url.searchParams.set('locale', translationLocale);
        const res = await globalAuthorizedFetch(url.toString());
        if (!res.ok || cancelled) return;
        const payload = (await res.json()) as {
          siteContent: Record<string, string>;
          landingSections: Array<{ sectionId: string; field: string; value: string }>;
        };
        if (!cancelled) {
          setSiteTranslations(payload.siteContent || {});
          setLandingSectionTranslationsJson(
            JSON.stringify(payload.landingSections || [], null, 2),
          );
        }
      } finally {
        if (!cancelled) setTranslationLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [globalAuthorizedFetch, translationLocale]);

  React.useEffect(() => {
    let cancelled = false;
    const q = (data?.publicContactCountry ?? '').trim();
    if (!q || q.length < 2) {
      setCountrySuggestions([]);
      return;
    }
    const timer = setTimeout(() => {
      const key = q.toLowerCase();
      const cached = countryCacheRef.current.get(key);
      if (cached) {
        if (!cancelled) setCountrySuggestions(cached.map((r) => r.name));
        return;
      }
      void (async () => {
        const res = await fetch(`${API_BASE}/auth/location/countries?q=${encodeURIComponent(q)}`);
        if (!res.ok || cancelled) return;
        const rows = (await res.json()) as Array<{ name: string; code?: string }>;
        countryCacheRef.current.set(key, rows);
        if (!cancelled) setCountrySuggestions(rows.map((r) => r.name));
      })();
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [data?.publicContactCountry]);

  React.useEffect(() => {
    let cancelled = false;
    const country = (data?.publicContactCountry ?? '').trim();
    const q = (data?.publicContactCity ?? '').trim();
    if (!country || country.length < 2 || q.length < 2) {
      setCitySuggestions([]);
      return;
    }
    const timer = setTimeout(() => {
      const key = `${country.toLowerCase()}:${q.toLowerCase()}`;
      const cached = cityCacheRef.current.get(key);
      if (cached) {
        if (!cancelled) setCitySuggestions(cached.map((r) => r.name));
        return;
      }
      void (async () => {
        const url = `${API_BASE}/auth/location/cities?country=${encodeURIComponent(country)}&countryCode=&q=${encodeURIComponent(q)}`;
        const res = await fetch(url);
        if (!res.ok || cancelled) return;
        const rows = (await res.json()) as Array<{ name: string }>;
        cityCacheRef.current.set(key, rows);
        if (!cancelled) setCitySuggestions(rows.map((r) => r.name));
      })();
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [data?.publicContactCountry, data?.publicContactCity]);

  React.useEffect(() => {
    let cancelled = false;
    const country = (data?.publicContactCountry ?? '').trim();
    const city = (data?.publicContactCity ?? '').trim();
    const q = (data?.publicContactAddress ?? '').trim();
    if (!country || country.length < 2 || city.length < 2 || q.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    const timer = setTimeout(() => {
      const key = `${country.toLowerCase()}::${city.toLowerCase()}::${q.toLowerCase()}`;
      const cached = addressCacheRef.current.get(key);
      if (cached) {
        if (!cancelled) setAddressSuggestions(cached);
        return;
      }
      void (async () => {
        const url = `${API_BASE}/auth/location/addresses?country=${encodeURIComponent(country)}&countryCode=&city=${encodeURIComponent(city)}&q=${encodeURIComponent(q)}`;
        const res = await fetch(url);
        if (!res.ok || cancelled) return;
        const rows = (await res.json()) as Array<{ label: string; street: string; city: string; country: string; postalCode: string; latitude?: number; longitude?: number }>;
        addressCacheRef.current.set(key, rows);
        if (!cancelled) setAddressSuggestions(rows);
      })();
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [data?.publicContactCountry, data?.publicContactCity, data?.publicContactAddress]);

  React.useEffect(() => {
    if (loading) return;
    if (location.hash !== '#public-contact') return;
    setActiveTab('contact');
    const el = publicContactRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [loading, location.hash]);

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const { landingSections: _omit, ...rest } = data;
      const res = await globalAuthorizedFetch(`${API_BASE}/settings/site-content`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...rest, landingSections: landingSections }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const msg =
          (body as { message?: string | string[] }).message ||
          (Array.isArray((body as { message?: string[] }).message)
            ? (body as { message: string[] }).message.join(' ')
            : undefined) ||
          'Unable to save.';
        throw new Error(typeof msg === 'string' ? msg : 'Unable to save.');
      }
      const payload = (await res.json()) as SiteContent;
      setData(payload);
      setLandingSections(
        cloneSections(payload.landingSections ?? DEFAULT_LANDING_SECTIONS),
      );
      setSuccess(
        t('common.saved_successfully', { defaultValue: 'Saved successfully.' }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to save.');
    } finally {
      setSaving(false);
    }
  };

  const saveTranslations = async () => {
    setTranslationSaving(true);
    setTranslationSuccess(null);
    setError(null);
    try {
      let landingSectionsPayload: Array<{
        sectionId: string;
        field: string;
        value?: string;
      }> = [];
      try {
        const parsed = JSON.parse(landingSectionTranslationsJson);
        if (Array.isArray(parsed)) {
          landingSectionsPayload = parsed;
        }
      } catch {
        throw new Error('Landing section translations must be valid JSON array.');
      }
      const res = await globalAuthorizedFetch(`${API_BASE}/settings/site-content/translations`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          locale: translationLocale,
          siteContent: siteTranslations,
          landingSections: landingSectionsPayload,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const msg = (body as { message?: string }).message || 'Unable to save translations.';
        throw new Error(msg);
      }
      setTranslationSuccess('Translation saved successfully.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to save translations.');
    } finally {
      setTranslationSaving(false);
    }
  };

  const setField = <K extends keyof SiteContent>(key: K, value: SiteContent[K]) => {
    setData((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const pickSuggestedPublicAddress = (label: string) => {
    if (!data) return;
    const match = addressSuggestions.find((a) => a.label === label);
    setField('publicContactAddress', label);
    if (match?.latitude != null && match?.longitude != null) {
      setField('publicContactLatitude', String(match.latitude));
      setField('publicContactLongitude', String(match.longitude));
    }
  };

  const filteredAddressLabels = React.useMemo(() => {
    if (!data) return [];
    return addressSuggestions.map((a) => a.label);
  }, [addressSuggestions, data]);

  return (
    <MainLayout withSidebar sidebarItems={getBackofficeSidebarItems(t)} activePath={location.pathname}>
      <main className="workspace-page">
        <section>
          <h1 className="workspace-page-title">
            {t('site_content_title', { defaultValue: 'Website content' })}
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            {t('site_content_subtitle', {
              defaultValue:
                'Edit About Us and legal pages (Terms, Privacy, Cookies, Payment Security).',
            })}
          </p>
        </section>
        <section className="max-w-5xl rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          {loading ? (
            <p className="text-sm text-slate-600">
              {t('common.loading', { defaultValue: 'Loading...' })}
            </p>
          ) : null}
          {error ? <p className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p> : null}
          {success ? <p className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{success}</p> : null}
          {!loading && data ? (
            <form onSubmit={onSave} className="grid gap-4">
              <div className="sticky top-2 z-10 -mx-1 overflow-x-auto rounded-xl border border-slate-200 bg-white/95 p-2 backdrop-blur">
                <div className="flex min-w-max gap-2">
                  {([
                    ['overview', t('site_content_tab_overview', { defaultValue: 'Overview' })],
                    ['contact', t('site_content_tab_public_contact', { defaultValue: 'Public contact' })],
                    ['legal', t('site_content_tab_legal', { defaultValue: 'Legal' })],
                    ['landing', t('site_content_tab_landing', { defaultValue: 'Landing sections' })],
                    ['translations', t('site_content_tab_translations', { defaultValue: 'Translations' })],
                  ] as Array<[SiteContentTabId, string]>).map(([tabId, label]) => (
                    <button
                      key={tabId}
                      type="button"
                      onClick={() => setActiveTab(tabId)}
                      className={
                        activeTab === tabId
                          ? 'rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white'
                          : 'rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:border-slate-400'
                      }
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {activeTab === 'overview' ? (
                <div className="grid gap-4">
                  <label className="text-xs font-semibold text-slate-700">
                    {t('site_content_about_title', { defaultValue: 'About title' })}
                    <input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={data.aboutTitle} onChange={(e) => setField('aboutTitle', e.target.value)} />
                  </label>
                  <label className="text-xs font-semibold text-slate-700">
                    {t('site_content_about_subtitle', { defaultValue: 'About subtitle' })}
                    <input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={data.aboutSubtitle} onChange={(e) => setField('aboutSubtitle', e.target.value)} />
                  </label>
                  <label className="text-xs font-semibold text-slate-700">
                    {t('site_content_about_image_url', { defaultValue: 'About image URL' })}
                    <input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={data.aboutImageUrl} onChange={(e) => setField('aboutImageUrl', e.target.value)} />
                  </label>
                  <label className="text-xs font-semibold text-slate-700">
                    About body
                    <textarea rows={8} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={data.aboutBody} onChange={(e) => setField('aboutBody', e.target.value)} />
                  </label>
                  <div className="grid gap-4 sm:grid-cols-3">
                    <label className="text-xs font-semibold text-slate-700">
                      Total customers (homepage stat)
                      <input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={data.storefrontStatCustomers} onChange={(e) => setField('storefrontStatCustomers', e.target.value)} />
                    </label>
                    <label className="text-xs font-semibold text-slate-700">
                      Total sales (homepage stat)
                      <input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={data.storefrontStatSales} onChange={(e) => setField('storefrontStatSales', e.target.value)} />
                    </label>
                    <label className="text-xs font-semibold text-slate-700">
                      Total towns served (homepage stat)
                      <input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={data.storefrontStatTowns} onChange={(e) => setField('storefrontStatTowns', e.target.value)} />
                    </label>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className="text-xs font-semibold text-slate-700">
                      Facebook URL
                      <input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={data.socialFacebookUrl} onChange={(e) => setField('socialFacebookUrl', e.target.value)} />
                    </label>
                    <label className="text-xs font-semibold text-slate-700">
                      Instagram URL
                      <input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={data.socialInstagramUrl} onChange={(e) => setField('socialInstagramUrl', e.target.value)} />
                    </label>
                    <label className="text-xs font-semibold text-slate-700">
                      LinkedIn URL
                      <input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={data.socialLinkedinUrl} onChange={(e) => setField('socialLinkedinUrl', e.target.value)} />
                    </label>
                    <label className="text-xs font-semibold text-slate-700">
                      X (Twitter) URL
                      <input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={data.socialXUrl} onChange={(e) => setField('socialXUrl', e.target.value)} />
                    </label>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <h2 className="text-sm font-semibold text-slate-900">
                  {t('backoffice.site_content_storefront_colors', {
                    defaultValue: 'Storefront colors',
                  })}
                </h2>
                <p className="mt-1 text-xs text-slate-600">
                  {t('backoffice.site_content_storefront_colors_hint', {
                    defaultValue:
                      'Page background and navbar styling on the public shop (same visual pickers as Landing promotion).',
                  })}
                </p>
                <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {(
                    [
                      [
                        'storefrontThemeBodyBg',
                        t('backoffice.site_content_color_body', {
                          defaultValue: 'Page background',
                        }),
                      ],
                      [
                        'storefrontThemeNavTopBg',
                        t('backoffice.site_content_color_nav_top', {
                          defaultValue: 'Top bar (contact & social)',
                        }),
                      ],
                      [
                        'storefrontThemeNavBg',
                        t('backoffice.site_content_color_nav', {
                          defaultValue: 'Main navbar background',
                        }),
                      ],
                      [
                        'storefrontThemeNavFg',
                        t('backoffice.site_content_color_nav_fg', {
                          defaultValue: 'Navbar text & icons',
                        }),
                      ],
                      [
                        'storefrontThemeBrandPrimary',
                        t('backoffice.site_content_color_brand', {
                          defaultValue: 'Brand primary (logo, map/mail icons, sign-in)',
                        }),
                      ],
                      [
                        'storefrontThemeNavAccent',
                        t('backoffice.site_content_color_accent', {
                          defaultValue: 'Accent (active links, logo highlight, register)',
                        }),
                      ],
                    ] as const
                  ).map(([key, label]) => {
                    const hex = toPickerHex(data[key]);
                    return (
                      <div key={key} className="workspace-stat-card">
                        <label
                          htmlFor={`site-theme-${key}`}
                          className="mb-3 block text-xs font-bold uppercase tracking-wide text-slate-800"
                        >
                          {label}
                        </label>
                        <div id={`site-theme-${key}`}>
                          <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                            <HexColorPicker
                              color={hex}
                              onChange={(next) =>
                                setField(key, next.toLowerCase())
                              }
                              style={{
                                width: '100%',
                                height: 'min(200px, 46vw)',
                                maxHeight: 200,
                              }}
                            />
                          </div>
                        </div>
                        <div className="mt-3 flex items-stretch gap-2">
                          <span
                            className="w-11 shrink-0 rounded-lg border border-slate-200 shadow-inner"
                            style={{ backgroundColor: hex }}
                            title={hex}
                            aria-hidden
                          />
                          <input
                            className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-[11px] text-slate-800"
                            value={data[key]}
                            onChange={(e) => setField(key, e.target.value)}
                            spellCheck={false}
                            maxLength={7}
                            aria-label={label}
                          />
                          <button
                            type="button"
                            className="shrink-0 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[11px] font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                            onClick={() => {
                              void navigator.clipboard?.writeText(hex);
                            }}
                          >
                            {t('backoffice.landing_promotion_copy_hex', {
                              defaultValue: 'Copy',
                            })}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
                </div>
              ) : null}

              {activeTab === 'contact' ? (
                <div
                id="public-contact"
                ref={publicContactRef}
                className="scroll-mt-24 rounded-lg border border-slate-200 bg-slate-50 p-4"
              >
                <h2 className="text-sm font-semibold text-slate-900">Public contact (storefront header)</h2>
                <p className="mt-1 text-xs text-slate-600">
                  This information is shown publicly on the customer site (address, email, phone).
                </p>
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  <label className="text-xs font-semibold text-slate-700">
                    Country (pick first)
                    <LocationSuggestInput
                      id="public-contact-country"
                      value={data.publicContactCountry}
                      onChange={(v) => {
                        setField('publicContactCountry', v);
                        setField('publicContactCity', '');
                        setField('publicContactAddress', '');
                        setField('publicContactLatitude', '');
                        setField('publicContactLongitude', '');
                      }}
                      suggestions={countrySuggestions}
                      placeholder="Start typing a country…"
                      inputClassName="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                      noDataHint="No countries available yet."
                      noMatchesHint="No matching country."
                      maxShown={60}
                    />
                  </label>
                  <label className="text-xs font-semibold text-slate-700">
                    Email
                    <input type="email" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={data.publicContactEmail} onChange={(e) => setField('publicContactEmail', e.target.value)} />
                  </label>
                  <label className="text-xs font-semibold text-slate-700">
                    City
                    <LocationSuggestInput
                      id="public-contact-city"
                      value={data.publicContactCity}
                      onChange={(v) => {
                        setField('publicContactCity', v);
                        setField('publicContactAddress', '');
                        setField('publicContactLatitude', '');
                        setField('publicContactLongitude', '');
                      }}
                      suggestions={citySuggestions}
                      placeholder={
                        data.publicContactCountry?.trim()
                          ? `Start typing a city in ${data.publicContactCountry.trim()}…`
                          : 'Pick a country first…'
                      }
                      inputClassName="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                      noDataHint={
                        data.publicContactCountry?.trim()
                          ? 'Type at least 2 letters to search cities.'
                          : 'Pick a country first.'
                      }
                      noMatchesHint="No matching city."
                      maxShown={60}
                    />
                  </label>
                  <label className="text-xs font-semibold text-slate-700">
                    Address (suggested)
                    <LocationSuggestInput
                      id="public-contact-address"
                      value={data.publicContactAddress}
                      onChange={(v) => {
                        setField('publicContactAddress', v);
                        pickSuggestedPublicAddress(v);
                      }}
                      suggestions={filteredAddressLabels}
                      placeholder={
                        data.publicContactCity?.trim()
                          ? `Start typing an address in ${data.publicContactCity.trim()}…`
                          : 'Pick a country and city first…'
                      }
                      inputClassName="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                      noDataHint={
                        data.publicContactCountry?.trim() && data.publicContactCity?.trim()
                          ? 'Type at least 3 letters to search addresses.'
                          : 'Pick a country and city first.'
                      }
                      noMatchesHint="No matching address."
                      maxShown={60}
                    />
                    <div className="mt-1 text-[11px] font-normal text-slate-500">
                      Pick a suggestion to auto-fill latitude/longitude.
                    </div>
                  </label>
                  <label className="text-xs font-semibold text-slate-700">
                    Phone
                    <input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={data.publicContactPhone} onChange={(e) => setField('publicContactPhone', e.target.value)} />
                  </label>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className="text-xs font-semibold text-slate-700">
                      Latitude (optional)
                      <input type="number" step="any" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={data.publicContactLatitude} onChange={(e) => setField('publicContactLatitude', e.target.value)} />
                    </label>
                    <label className="text-xs font-semibold text-slate-700">
                      Longitude (optional)
                      <input type="number" step="any" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={data.publicContactLongitude} onChange={(e) => setField('publicContactLongitude', e.target.value)} />
                    </label>
                  </div>
                </div>
                </div>
              ) : null}

              {activeTab === 'legal' ? (
                <div className="grid gap-4">
                  <label className="text-xs font-semibold text-slate-700">
                    Terms title
                    <input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={data.termsTitle} onChange={(e) => setField('termsTitle', e.target.value)} />
                  </label>
                  <label className="text-xs font-semibold text-slate-700">
                    Terms body
                    <textarea rows={14} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={data.termsBody} onChange={(e) => setField('termsBody', e.target.value)} />
                  </label>
                  <label className="text-xs font-semibold text-slate-700">
                    Privacy title
                    <input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={data.privacyTitle} onChange={(e) => setField('privacyTitle', e.target.value)} />
                  </label>
                  <label className="text-xs font-semibold text-slate-700">
                    Privacy body
                    <textarea rows={12} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={data.privacyBody} onChange={(e) => setField('privacyBody', e.target.value)} />
                  </label>
                  <label className="text-xs font-semibold text-slate-700">
                    Cookies title
                    <input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={data.cookiesTitle} onChange={(e) => setField('cookiesTitle', e.target.value)} />
                  </label>
                  <label className="text-xs font-semibold text-slate-700">
                    Cookies body
                    <textarea rows={8} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={data.cookiesBody} onChange={(e) => setField('cookiesBody', e.target.value)} />
                  </label>
                  <label className="text-xs font-semibold text-slate-700">
                    Payment security title
                    <input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={data.paymentSecurityTitle} onChange={(e) => setField('paymentSecurityTitle', e.target.value)} />
                  </label>
                  <label className="text-xs font-semibold text-slate-700">
                    Payment security body
                    <textarea rows={8} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={data.paymentSecurityBody} onChange={(e) => setField('paymentSecurityBody', e.target.value)} />
                  </label>
                </div>
              ) : null}

              {activeTab === 'landing' ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <LandingSectionsEditor
                  sections={landingSections}
                  onChange={setLandingSections}
                  authorizedFetch={globalAuthorizedFetch}
                  apiBase={API_BASE}
                  onResetDefaults={() => setLandingSections(cloneSections(DEFAULT_LANDING_SECTIONS))}
                />
                </div>
              ) : null}

              {activeTab === 'translations' ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <h2 className="text-sm font-semibold text-slate-900">
                    {t('dynamic_content_translations', {
                      defaultValue: 'Dynamic content translations',
                    })}
                  </h2>
                  <select
                    value={translationLocale}
                    onChange={(e) => {
                      setTranslationLocale(e.target.value);
                      setTranslationSuccess(null);
                    }}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs"
                  >
                    <option value="fr">{t('lang.fr', { defaultValue: 'French (fr)' })}</option>
                    <option value="es">{t('lang.es', { defaultValue: 'Spanish (es)' })}</option>
                    <option value="de">{t('lang.de', { defaultValue: 'German (de)' })}</option>
                  </select>
                </div>
                {translationLoading ? (
                  <p className="text-xs text-slate-500">
                    {t('loading_translations', { defaultValue: 'Loading translations...' })}
                  </p>
                ) : (
                  <div className="grid gap-3">
                    {[
                      ['aboutTitle', 'About title'],
                      ['aboutSubtitle', 'About subtitle'],
                      ['aboutBody', 'About body'],
                      ['storefrontStatCustomers', 'Storefront customers stat'],
                      ['storefrontStatSales', 'Storefront sales stat'],
                      ['storefrontStatTowns', 'Storefront towns stat'],
                      ['termsTitle', 'Terms title'],
                      ['privacyTitle', 'Privacy title'],
                      ['cookiesTitle', 'Cookies title'],
                      ['paymentSecurityTitle', 'Payment security title'],
                    ].map(([key, label]) => (
                      <label key={key} className="text-xs font-semibold text-slate-700">
                        {label}
                        <input
                          className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                          value={siteTranslations[key] || ''}
                          onChange={(e) => {
                            setSiteTranslations((p) => ({ ...p, [key]: e.target.value }));
                            setTranslationSuccess(null);
                          }}
                        />
                      </label>
                    ))}
                    <label className="text-xs font-semibold text-slate-700">
                      {t('landing_section_translations_json', {
                        defaultValue: 'Landing section translations JSON',
                      })}
                      <textarea
                        rows={8}
                        className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-xs"
                        value={landingSectionTranslationsJson}
                        onChange={(e) => {
                          setLandingSectionTranslationsJson(e.target.value);
                          setTranslationSuccess(null);
                        }}
                      />
                    </label>
                    <div>
                      <button
                        type="button"
                        onClick={saveTranslations}
                        disabled={translationSaving}
                        className="rounded-md border border-brand-500 bg-white px-4 py-2 text-xs font-semibold text-brand-700 hover:bg-brand-50 disabled:opacity-60"
                      >
                          {translationSaving
                            ? t('saving_translation', {
                                defaultValue: 'Saving translation...',
                              })
                            : t('save_translation', {
                                defaultValue: 'Save translation',
                              })}
                      </button>
                      {translationSuccess ? (
                        <p className="mt-2 text-xs font-medium text-emerald-700">
                          {translationSuccess}
                        </p>
                      ) : null}
                    </div>
                  </div>
                )}
                </div>
              ) : null}
              <div className="sticky bottom-2">
                <button type="submit" disabled={saving} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                  {saving
                    ? t('common.saving', { defaultValue: 'Saving...' })
                    : t('site_content_save_content', { defaultValue: 'Save content' })}
                </button>
              </div>
            </form>
          ) : null}
        </section>
      </main>
    </MainLayout>
  );
}

